import React, { useEffect, useState } from "react";
import { useHistory, Link } from "react-router-dom";
import { Alert, Badge, Button, FormGroup, Input, Label, Spinner } from "reactstrap";
import MyCollectionLayout from "./MyCollectionLayout";
import McBackLink from "./McBackLink";
import {
  getCollectorMe,
  setCollectorPasskey,
  updateCollectorProfile,
  listArchivedCollectorItems,
  restoreCollectorItem,
  signOutCollector,
  isCollectorSessionError,
} from "../../services/collectorApi";
import { clearCollectorToken, collectorLoginPath, getCollectorToken } from "../../helpers/collectorSession";
import { collectorRecordKey } from "../../helpers/collectorIds";
import CollectorMasterCuration from "./CollectorMasterCuration";
import CollectorPhoneSettings from "./CollectorPhoneSettings";

type Account = {
  phone?: string;
  displayName?: string;
  email?: string;
  plan?: string;
  trialEndsAt?: string;
  trialDaysRemaining?: number;
  defaultQrVisibility?: string;
  hasPasskey?: boolean;
  canCurateMasters?: boolean;
  externalOverLimit?: boolean;
  externalItemLimit?: number | null;
};

type Feedback = { type: "success" | "danger"; text: string } | null;

type ArchivedItem = {
  id?: string;
  _id?: string;
  title?: string;
  itemType?: string;
  archivedAt?: string;
};

const MyCollectionSettings: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState<Account | null>(null);
  const [profile, setProfile] = useState({ displayName: "", email: "", defaultQrVisibility: "PUBLIC" });
  const [signingOut, setSigningOut] = useState(false);
  const [passkey, setPasskey] = useState({ current: "", next: "", confirm: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPasskey, setSavingPasskey] = useState(false);
  const [profileMsg, setProfileMsg] = useState<Feedback>(null);
  const [passkeyMsg, setPasskeyMsg] = useState<Feedback>(null);
  const [archived, setArchived] = useState<ArchivedItem[]>([]);
  const [archivedMsg, setArchivedMsg] = useState<Feedback>(null);
  const [restoringId, setRestoringId] = useState("");

  const loadArchived = () =>
    listArchivedCollectorItems()
      .then((res) => setArchived(res.data?.data?.items || []))
      .catch(() => setArchivedMsg({ type: "danger", text: "Could not load archived items" }));

  const loadAccount = () =>
    getCollectorMe()
      .then((res) => {
        const data: Account = res.data?.data || {};
        setAccount(data);
        setProfile({
          displayName: data.displayName || "",
          email: data.email || "",
          defaultQrVisibility: data.defaultQrVisibility || "PUBLIC",
        });
      })
      .catch((err) => {
        if (isCollectorSessionError(err)) {
          history.push(collectorLoginPath(history.location.pathname + history.location.search));
          return;
        }
        setProfileMsg({ type: "danger", text: "Could not load your account" });
      });

  useEffect(() => {
    if (!getCollectorToken()) {
      history.push(collectorLoginPath(history.location.pathname + history.location.search));
      return;
    }
    loadAccount().finally(() => setLoading(false));
    loadArchived();
  }, [history]);

  const errorText = (e: any, fallback: string) =>
    e?.response?.data?.message || e?.response?.data?.error || fallback;

  const saveProfile = async () => {
    setSavingProfile(true);
    setProfileMsg(null);
    const email = profile.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setProfileMsg({ type: "danger", text: "Enter a valid email address" });
      setSavingProfile(false);
      return;
    }
    try {
      const res = await updateCollectorProfile({ ...profile, email });
      const data = res.data?.data || {};
      setAccount((prev) => ({ ...(prev || {}), ...data }));
      setProfileMsg({ type: "success", text: "Settings saved" });
    } catch (e: any) {
      setProfileMsg({ type: "danger", text: errorText(e, "Could not save settings") });
    } finally {
      setSavingProfile(false);
    }
  };

  const savePasskey = async () => {
    if (passkey.next.length < 4) {
      setPasskeyMsg({ type: "danger", text: "Passkey must be at least 4 characters" });
      return;
    }
    if (passkey.next !== passkey.confirm) {
      setPasskeyMsg({ type: "danger", text: "Passkeys do not match" });
      return;
    }
    setSavingPasskey(true);
    setPasskeyMsg(null);
    try {
      await setCollectorPasskey(passkey.next, passkey.current || undefined);
      setAccount((prev) => ({ ...(prev || {}), hasPasskey: true }));
      setPasskey({ current: "", next: "", confirm: "" });
      setPasskeyMsg({ type: "success", text: "Passkey updated" });
    } catch (e: any) {
      setPasskeyMsg({ type: "danger", text: errorText(e, "Could not update passkey") });
    } finally {
      setSavingPasskey(false);
    }
  };

  const restore = async (item: ArchivedItem) => {
    const id = collectorRecordKey(item);
    if (!id) return;
    setRestoringId(id);
    setArchivedMsg(null);
    try {
      await restoreCollectorItem(id);
      setArchivedMsg({ type: "success", text: `"${item.title || "Piece"}" restored to My Collection` });
      await loadArchived();
    } catch (e: any) {
      setArchivedMsg({ type: "danger", text: errorText(e, "Could not restore this piece") });
    } finally {
      setRestoringId("");
    }
  };

  const logout = async () => {
    setSigningOut(true);
    let endedEverywhere = true;
    try {
      await signOutCollector();
    } catch {
      // Still sign out on this phone, but say so: other devices may still be signed in.
      endedEverywhere = false;
    }
    // Ending the session also stops photo archiving (collectorMediaUpload listens for it).
    clearCollectorToken();
    history.push("/my-collection/login", endedEverywhere ? undefined : { signOutIncomplete: true });
  };

  if (loading) {
    return (
      <MyCollectionLayout>
        <div className="text-center py-5"><Spinner /></div>
      </MyCollectionLayout>
    );
  }

  const hasPasskey = Boolean(account?.hasPasskey);
  const isPaidTier = account?.plan === "TRIAL" || account?.plan === "PREMIUM";
  const daysLeft = account?.trialDaysRemaining || 0;
  const isTrial = account?.plan === "TRIAL";
  const isFreeAfterTrial = account?.plan === "FREE" && Boolean(account?.trialEndsAt);
  const showLastDay = isTrial && daysLeft === 1;
  const showSevenDay = isTrial && daysLeft > 1 && daysLeft <= 7;

  return (
    <MyCollectionLayout>
      <McBackLink to="/my-collection">Back to My pieces</McBackLink>
      <h1 className="h3 mb-1">Settings</h1>
      <p className="text-muted small mb-3">
        Plan: <Badge color="secondary">{account?.plan || "FREE"}</Badge>
        {account?.trialEndsAt && account?.plan === "TRIAL" && (
          <span className="ms-2">Trial ends {new Date(account.trialEndsAt).toLocaleDateString()}</span>
        )}
        {" · "}
        <Link to="/my-collection/upgrade">Upgrade</Link>
      </p>

      {showSevenDay && (
        <Alert color="warning">
          Your trial has {daysLeft} days left.{" "}
          <Link to="/my-collection/upgrade">Request premium access</Link> to keep private QR pages.
        </Alert>
      )}
      {showLastDay && (
        <Alert color="danger">
          Last day of your trial. Tomorrow you move to the free plan. Your pieces stay yours and stay editable.{" "}
          <Link to="/my-collection/upgrade">Upgrade now</Link>
        </Alert>
      )}
      {isFreeAfterTrial && (
        <Alert color="secondary">
          Your trial has ended. You are on the free plan. Scans and your own pieces stay unlimited.{" "}
          <Link to="/my-collection/upgrade">See premium</Link>
        </Alert>
      )}
      {account?.externalOverLimit && (
        <Alert color="warning">
          You have more than {account.externalItemLimit} pieces in My pieces. You can still edit all of them;
          new pieces need premium.{" "}
          <Link to="/my-collection/upgrade">Upgrade</Link>
        </Alert>
      )}

      <div className="mc-settings-grid">
        <section className="mc-card">
          <h2 className="h6 text-uppercase text-muted mb-3">Profile</h2>
          {profileMsg && <Alert color={profileMsg.type}>{profileMsg.text}</Alert>}
          <FormGroup>
            <Label for="displayName">Display name</Label>
            <Input
              id="displayName"
              value={profile.displayName}
              onChange={(e) => setProfile({ ...profile, displayName: e.target.value })}
              placeholder="Your name"
            />
          </FormGroup>
          <FormGroup>
            <Label for="email">Email (optional)</Label>
            <Input
              id="email"
              type="email"
              value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              placeholder="you@example.com"
            />
            <small className="text-muted">Used only for account recovery and receipts.</small>
          </FormGroup>
          <FormGroup>
            <Label for="defaultQrVisibility">Default visibility for new pieces</Label>
            <Input
              id="defaultQrVisibility"
              type="select"
              value={profile.defaultQrVisibility}
              onChange={(e) => setProfile({ ...profile, defaultQrVisibility: e.target.value })}
            >
              <option value="PUBLIC">Public — anyone scanning the QR sees the story</option>
              <option value="PRIVATE" disabled={!hasPasskey}>
                Private — passkey required to view
              </option>
            </Input>
            {!hasPasskey && (
              <small className="text-muted">Set a passkey to enable private pieces.</small>
            )}
          </FormGroup>
          <Button className="mc-btn-primary" onClick={saveProfile} disabled={savingProfile}>
            {savingProfile ? "Saving…" : "Save changes"}
          </Button>
        </section>

        <CollectorPhoneSettings
          currentPhone={account?.phone}
          hasPasskey={Boolean(account?.hasPasskey)}
          onAccountChanged={() => {
            loadAccount();
            loadArchived();
          }}
        />

        <section className="mc-card">
          <h2 className="h6 text-uppercase text-muted mb-1">Account passkey</h2>
          <p className="small text-muted">
            {hasPasskey
              ? "A passkey is set. Visitors need it to open your private pieces."
              : "Set a passkey to keep chosen pieces private behind your QR codes."}
          </p>
          {passkeyMsg && <Alert color={passkeyMsg.type}>{passkeyMsg.text}</Alert>}
          {!isPaidTier && (
            <Alert color="warning" className="small">
              Private pieces are available on trial and premium plans.{" "}
              <Link to="/my-collection/upgrade">See premium</Link>
            </Alert>
          )}
          {hasPasskey && (
            <FormGroup>
              <Label for="currentPasskey">Current passkey</Label>
              <Input
                id="currentPasskey"
                type="password"
                autoComplete="current-password"
                value={passkey.current}
                onChange={(e) => setPasskey({ ...passkey, current: e.target.value })}
              />
            </FormGroup>
          )}
          <FormGroup>
            <Label for="newPasskey">{hasPasskey ? "New passkey" : "Passkey"}</Label>
            <Input
              id="newPasskey"
              type="password"
              autoComplete="new-password"
              value={passkey.next}
              onChange={(e) => setPasskey({ ...passkey, next: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <Label for="confirmPasskey">Confirm passkey</Label>
            <Input
              id="confirmPasskey"
              type="password"
              autoComplete="new-password"
              value={passkey.confirm}
              onChange={(e) => setPasskey({ ...passkey, confirm: e.target.value })}
            />
          </FormGroup>
          <Button className="mc-btn-primary" onClick={savePasskey} disabled={savingPasskey || !isPaidTier}>
            {savingPasskey ? "Saving…" : hasPasskey ? "Change passkey" : "Set passkey"}
          </Button>
        </section>

        <section className="mc-card">
          <h2 className="h6 text-uppercase text-muted mb-2">Archived items</h2>
          <p className="small text-muted">
            Archived pieces are hidden from My pieces and Scanned. Public QR pages show “no longer available” until you restore them.
          </p>
          {archivedMsg && <Alert color={archivedMsg.type}>{archivedMsg.text}</Alert>}
          {archived.length === 0 ? (
            <p className="small text-muted mb-0">Nothing archived.</p>
          ) : (
            <ul className="list-unstyled mb-0">
              {archived.map((item) => {
                const id = collectorRecordKey(item);
                return (
                  <li key={id} className="d-flex justify-content-between align-items-center border-bottom py-2">
                    <div>
                      <div className="fw-semibold">{item.title || "Untitled"}</div>
                      <small className="text-muted">
                        {item.itemType}
                        {item.archivedAt ? ` · archived ${new Date(item.archivedAt).toLocaleDateString()}` : ""}
                      </small>
                    </div>
                    <Button className="mc-btn-secondary" disabled={restoringId === id} onClick={() => restore(item)}>
                      {restoringId === id ? "Restoring…" : "Restore"}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {account?.canCurateMasters && <CollectorMasterCuration />}
      </div>

      <p className="small text-muted mt-3 mb-0">
        Questions? <Link to="/my-collection/help">Open Help</Link> for FAQs and support.
      </p>

      <div className="mt-3">
        <Button className="mc-btn-ghost" onClick={logout} disabled={signingOut}>
          {signingOut ? "Logging out…" : "Log out"}
        </Button>
      </div>
    </MyCollectionLayout>
  );
};

export default MyCollectionSettings;
